import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface ContactFormValues {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const ContactForm = () => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<ContactFormValues>({
    defaultValues: { name: '', email: '', subject: '', message: '' },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    const failsafe = setTimeout(() => setIsSubmitting(false), 25000);
    try {
      const { data: result, error } = await supabase.functions.invoke('send-contact-email', {
        body: data,
      });

      if (error || (result && (result as { error?: string }).error)) {
        const message = (result as { error?: string } | null)?.error
          || error?.message
          || 'Please try again later or contact us directly.';
        toast({
          title: 'Error sending message',
          description: message,
          variant: 'destructive',
          duration: 5000,
        });
        console.error('Form submission error:', error ?? result);
        return;
      }

      toast({
        title: 'Thank you for your enquiry',
        description: 'We aim to reply as soon as possible.',
        duration: 5000,
      });
      form.reset();
    } catch (err) {
      console.error('Error sending form:', err);
      toast({
        title: 'Error sending message',
        description: 'Please try again later or contact us directly.',
        variant: 'destructive',
        duration: 5000,
      });
    } finally {
      clearTimeout(failsafe);
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full bg-background/50 border-border focus:border-primary text-foreground placeholder:text-muted-foreground/60';

  return (
    <div className="glass-card p-8 animate-fade-in-up opacity-0" style={{ animationDelay: '0.4s' }}>
      <h3 className="font-display text-2xl font-bold mb-6 text-foreground">Send Us a Message</h3>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground">Name</FormLabel>
                  <FormControl>
                    <Input {...field} className={inputClass} placeholder="John Doe" required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-foreground">Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" className={inputClass} placeholder="john@example.com" required />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="subject"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">Subject</FormLabel>
                <FormControl>
                  <Input {...field} className={inputClass} placeholder="How can we help you?" required />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-foreground">Message</FormLabel>
                <FormControl>
                  <Textarea {...field} rows={5} className={inputClass} placeholder="Tell us about your project..." required />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button type="submit" disabled={isSubmitting} variant="hero" size="lg" className="w-full">
            {isSubmitting ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default ContactForm;
